/**
 * @file useApproveHereOnly.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Xero refused an invoice's approval (or an approved invoice on
 *              its way): ask once, for one or many, whether to approve it
 *              here only — on statements now, sent to Xero once Xero takes
 *              it, a persistent alert until then (Cam, 3 Nov 2026). Never
 *              automatic: the person answers.
 *
 *   const { ask, dialog } = useApproveHereOnly();
 *   const yes = await ask([{ invoice_no, reason }, …]);   // true | false
 *   … render {dialog}
 */

import React, { useCallback, useRef, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import { refusedQuestion } from '../utils/heldHere';

export default function useApproveHereOnly() {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const ask = useCallback((entries) => new Promise((resolve) => {
    const list = (Array.isArray(entries) ? entries : [entries]).filter(Boolean);
    if (!list.length) { resolve(false); return; }
    resolver.current = resolve;
    setState({ entries: list });
  }), []);

  const finish = (answer) => {
    const r = resolver.current;
    resolver.current = null;
    setState(null);
    if (r) r(answer);
  };

  let dialog = null;
  if (state) {
    const q = refusedQuestion(state.entries);
    dialog = (
      <ConfirmDialog
        isOpen
        title={q.title}
        message={<p style={{ whiteSpace: 'pre-line' }}>{q.message}</p>}
        type="warning"
        confirmText={q.confirmText}
        confirmIcon="cloud_sync"
        cancelText={state.entries.length === 1 ? 'Leave it' : 'Leave them'}
        onConfirm={() => finish(true)}
        onCancel={() => finish(false)}
      />
    );
  }
  return { ask, dialog };
}
